import { useCallback, useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import Link from "next/link";
import { Line } from "rc-progress";
import ReactLoading from "react-loading";
import axios from "axios";
import clsx from "clsx";
import { browserName } from "react-device-detect";

import { AuthContext } from "@/contexts/auth";
import { TaskContext } from "@/contexts/task";
import arrowIcon from "@/assets/icons/arrow-down-black.svg";
import mypageIcon from "@/assets/icons/mypage.svg";
import { debugLog } from "@/utils/debugLog";
import { useRecBrowserJudge } from "@/hooks/common/useRecBrowserJudge";
import { extractionBrowserName } from "@/utils/extractionBrowserName";
import useUserIcon from "@/hooks/useUserIcon";
import Skeleton from "@/components/shared/skeleton";

import styles from "./index.module.scss";

type Props = {
  text?: string | JSX.Element;
  children?: React.ReactNode;
  hideBack?: boolean;
  clickBack?: (e: any) => void;
  profileImage?: boolean;
};
type Owner = {
  id: number;
  is_summary: boolean;
  username: string;
};

const Header: React.FC<Props> = ({
  text,
  children,
  hideBack,
  clickBack,
  profileImage
}) => {
  const router = useRouter();
  const { downloadData } = useContext(TaskContext);
  const { token, summaryAccounts, userId, userType } = useContext(AuthContext);
  const [showSummaryLoading, setShowSummaryLoading] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const { isRecommendedBrowser } = useRecBrowserJudge();
  const { icon, isLoading } = useUserIcon();

  //MEMO: ブラウザごとの色を指定（ここに書かないと反映しない）
  const browserColors = {
    chrome: "border-[#f3b80b]",
    firefox: "border-[#ff7139]",
    safari: "border-[#007AFF]",
    edge: "border-[#0078d7]",
    opera: "border-[#FF1B2D]",
    samsung: "border-[#1428a0]",
    line: "border-[#00B900]",
    facebook: "border-[#1877F2]",
    instagram: "border-[#E4405F]",
    twitter: "border-[#1DA1F2]",
    slack: "border-[#4A154B]",
    Unknown: ""
  } as { [key: string]: string };

  const changeAccount = useCallback(
    (selectedId: string) => {
      setShowSummaryLoading(true);
      const usertype = userType === "Owner" ? "owner" : "crew";
      axios
        .post(
          `${process.env.APP_API_PATH}/${usertype}/switch-user`,
          { id: selectedId },
          token
        )
        .then((res) => {
          debugLog(res.data);
          setShowSummaryLoading(false);
          if (
            summaryAccounts.switchable_users.find(
              (owner: Owner) => owner.id === Number(selectedId)
            ).is_summary
          ) {
            if (router.pathname === `/${usertype}/dashboard`) {
              window.location.reload();
            } else {
              router.push(`/${usertype}/dashboard`);
            }
          } else {
            window.location.reload();
          }
        })
        .catch((e) => {
          console.error(e);
          setShowSummaryLoading(false);
        });
    },
    [userType, summaryAccounts]
  );

  useEffect(() => {
    if (!hideBack && (window.history.state?.idx !== 0 || clickBack))
      setShowBack(true);
  }, [hideBack]);

  return (
    <>
      <header
        className={clsx(
          styles.header,
          isRecommendedBrowser ? "border-r-4" : "border-t-4",
          browserColors[extractionBrowserName(browserName)]
        )}
      >
        {summaryAccounts.account_type !== "normal" && (
          <div className={styles.summary_account_header}>
            <div className={styles.summary_account_header_text}>
              サマリーアカウント表示中
            </div>
            <div className={styles.select_wrapper}>
              <select
                className={styles.input}
                defaultValue={userId}
                onChange={(e) => {
                  if (e.target.value) {
                    changeAccount(e.target.value);
                  }
                }}
                name="summary"
              >
                <option value={userId}>{`${
                  summaryAccounts.is_summary ? "*" : ""
                }${summaryAccounts.username}`}</option>
                {summaryAccounts.switchable_users.map((owner: Owner) => {
                  return (
                    <option value={owner.id} key={owner.id}>
                      {`${owner.is_summary ? "*" : ""}${owner.username}`}
                    </option>
                  );
                })}
              </select>
              {!showSummaryLoading ? (
                <div className={styles.arrow_wrapper}>
                  <Image src={arrowIcon} alt="矢印" />
                </div>
              ) : (
                <div
                  className={styles.loading_wrapper}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div className={styles.loading}>
                    <ReactLoading
                      type="spinningBubbles"
                      color="#505050"
                      width="1rem"
                      height="1rem"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <Line
          className={downloadData.status ? styles.progress_bar : styles.hidden}
          percent={
            downloadData.status
              ? downloadData.progress >= 90
                ? 90 //90%以上は90%に固定（100%になっても圧縮処理などがあるため）
                : downloadData.progress
              : 0
          }
          strokeWidth={0.4}
          strokeColor="#ec6b6b"
          trailWidth={0.4}
          trailColor="#fafafa"
        />
        <div className="relative flex h-40 w-full items-center justify-between">
          <div
            className={clsx(
              showBack ? styles.back : styles.hidden,
              "absolute left-12"
            )}
            onClick={(e) => (clickBack ? clickBack(e) : router.back())}
          ></div>
          <h1 className={clsx(styles.title, "mx-auto")}>{text}</h1>
          {children}
          {profileImage && (
            <div className="absolute right-8 top-1/2 h-[3rem] -translate-y-1/2">
              {isLoading ? (
                <Skeleton
                  width={30}
                  height={30}
                  className="inline-block rounded-full"
                  containerClassName="h-[3rem]"
                />
              ) : (
                <Link
                  href={`/${userType === "Owner" ? "owner" : "crew"}/mypage`}
                  passHref
                >
                  <a className="inline-block">
                    <Image
                      src={
                        icon
                          ? `${process.env.APP_PHOTO_URL}/${icon}`
                          : mypageIcon
                      }
                      alt="User Icon"
                      width={30}
                      height={30}
                      className={clsx(
                        "rounded-full",
                        icon && "border border-solid border-borderDivider"
                      )}
                    />
                  </a>
                </Link>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
